"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Localization = void 0;
const libs_i18n_core_1 = require("libs.i18n-core");
const types_1 = require("./types");
const utils_1 = require("./utils");
function deepEqual(obj1, obj2) {
    if (obj1 === obj2) {
        return true;
    }
    if (typeof obj1 !== 'object' || obj1 === null || typeof obj2 !== 'object' || obj2 === null) {
        return false;
    }
    const keys1 = Object.keys(obj1);
    const keys2 = Object.keys(obj2);
    if (keys1.length !== keys2.length) {
        return false;
    }
    for (const key of keys1) {
        if (!keys2.includes(key) || !deepEqual(obj1[key], obj2[key])) {
            return false;
        }
    }
    return true;
}
const defaultInitBaseLocale = libs_i18n_core_1.Locale.en_US;
/**
 * See documentation at docs/libs/localization.md
 */
class Localization {
    static mapImportLocalizationOrigin(sourceOrigin) {
        switch (sourceOrigin) {
            case types_1.LocalizationOrigin.AI_TRANSLATION:
            case types_1.LocalizationOrigin.IMPORT_FROM_LOCALIZATION_ASSET_AI_TRANSLATION:
                return types_1.LocalizationOrigin.IMPORT_FROM_LOCALIZATION_ASSET_AI_TRANSLATION;
            case types_1.LocalizationOrigin.AI_MIGRATION:
            case types_1.LocalizationOrigin.IMPORT_FROM_LOCALIZATION_ASSET_AI_MIGRATION:
                return types_1.LocalizationOrigin.IMPORT_FROM_LOCALIZATION_ASSET_AI_MIGRATION;
            case types_1.LocalizationOrigin.USER:
            case types_1.LocalizationOrigin.IMPORT_FROM_LOCALIZATION_ASSET_USER:
                return types_1.LocalizationOrigin.IMPORT_FROM_LOCALIZATION_ASSET_USER;
            default:
                return types_1.LocalizationOrigin.IMPORT_FROM_LOCALIZATION_ASSET_USER;
        }
    }
    constructor(assetOrId, options = {}) {
        var _a;
        this.transactionLocaleVersions = new WeakMap();
        let displayName;
        const { baseLocale } = options;
        if (typeof assetOrId === 'string') {
            this.id = assetOrId;
            if (assetOrId.startsWith('product_')) {
                displayName = assetOrId;
            }
        }
        else {
            this.id = (0, utils_1.assetToId)(assetOrId);
            displayName = (0, utils_1.assetToDisplayName)(assetOrId);
        }
        this.initBaseLocale = baseLocale;
        // Prefer `displayName`; fall back to the deprecated `humanId` option for backward compatibility.
        this.initDisplayName = (_a = displayName !== null && displayName !== void 0 ? displayName : options.displayName) !== null && _a !== void 0 ? _a : options.humanId;
    }
    setMessages(locale_1, namespaces_1) {
        return __awaiter(this, arguments, void 0, function* (locale, namespaces, origin = types_1.LocalizationOrigin.USER, originDetails, options) {
            var _a, _b;
            const transaction = options === null || options === void 0 ? void 0 : options.transaction;
            // Prepare a cleanup payload to null-out specific keys before merging new data.
            // This replaces the previous cleanedUpEntry + setDatabaseEntry approach to avoid races.
            const cleanupData = {};
            let hasCleanup = false;
            const data = {};
            const localizationResourceDataSet = new Set();
            const entry = yield this.getDatabaseEntry();
            const baseLocale = yield this.getBaseLocale(entry);
            const displayName = (_a = (0, utils_1.getDocumentDisplayName)(entry)) !== null && _a !== void 0 ? _a : this.initDisplayName;
            let didSetLocalizationResourceRun = false;
            if (namespaces.some(({ namespace }) => namespace === '_identity')) {
                throw new Error('_identity is a reserved namespace name.');
            }
            const setInitialData = () => {
                data.base_locale = baseLocale;
                data.display_name = displayName;
            };
            const setLocalizationResource = () => {
                didSetLocalizationResourceRun = true;
                // Cleanup: ensure old localization resource for this locale is cleared before merging new content.
                // We write `null` so downstream database adapters can interpret this as a deletion.
                cleanupData.localization_resources = { [locale]: null };
                hasCleanup = hasCleanup || !!entry; // Only attempt cleanup if a document exists.
                data.localization_resources = {
                    [locale]: Object.assign({}, ...namespaces.map(({ namespace, messages }) => ({
                        [namespace]: messages,
                    }))),
                };
            };
            let isDiffCache;
            const isDiff = () => {
                var _a, _b;
                if (isDiffCache === undefined) {
                    const currLocalizationResource = (_a = entry === null || entry === void 0 ? void 0 : entry.localization_resources) === null || _a === void 0 ? void 0 : _a[locale];
                    if (((_b = data.localization_resources) === null || _b === void 0 ? void 0 : _b[locale]) && currLocalizationResource) {
                        isDiffCache = !deepEqual(data.localization_resources[locale], currLocalizationResource);
                    }
                    else {
                        isDiffCache = false;
                    }
                }
                return isDiffCache;
            };
            const invalidateAllLocales = () => {
                var _a, _b, _c, _d, _e, _f, _g, _h, _j;
                if (!isDiff()) {
                    return;
                }
                data.supported_locales = (_a = data.supported_locales) !== null && _a !== void 0 ? _a : {};
                for (const supportedLocale of Object.keys((_b = entry === null || entry === void 0 ? void 0 : entry.supported_locales) !== null && _b !== void 0 ? _b : {})) {
                    if (supportedLocale === baseLocale) {
                        continue;
                    }
                    if (((_d = (_c = entry === null || entry === void 0 ? void 0 : entry.supported_locales) === null || _c === void 0 ? void 0 : _c[supportedLocale]) === null || _d === void 0 ? void 0 : _d.status) === types_1.LocalizationStatus.READY) {
                        const currVersion = (_f = (_e = entry === null || entry === void 0 ? void 0 : entry.supported_locales) === null || _e === void 0 ? void 0 : _e[supportedLocale]) === null || _f === void 0 ? void 0 : _f.version;
                        if (!currVersion) {
                            throw new Error('Internal error');
                        }
                        const last_updated = new Date();
                        data.supported_locales[supportedLocale] = {
                            locale: supportedLocale,
                            last_updated,
                            last_exported_at: null,
                            version: currVersion + 1,
                            status: types_1.LocalizationStatus.IN_PROGRESS,
                            localization_origin: origin,
                            localization_origin_details: originDetails !== null && originDetails !== void 0 ? originDetails : null,
                        };
                        const baseLocaleVersion = (_h = (_g = entry === null || entry === void 0 ? void 0 : entry.supported_locales) === null || _g === void 0 ? void 0 : _g[baseLocale]) === null || _h === void 0 ? void 0 : _h.version;
                        if (!baseLocaleVersion) {
                            throw new Error('Internal error');
                        }
                        localizationResourceDataSet.add({
                            locale: supportedLocale,
                            last_updated,
                            base_locale_version: baseLocaleVersion + 1,
                            version: currVersion + 1,
                            status: types_1.LocalizationStatus.IN_PROGRESS,
                            localization_resource: (_j = entry === null || entry === void 0 ? void 0 : entry.localization_resources) === null || _j === void 0 ? void 0 : _j[supportedLocale],
                            localization_origin: origin,
                            localization_origin_details: originDetails !== null && originDetails !== void 0 ? originDetails : null,
                        });
                    }
                }
            };
            const setSupportedLocale = () => {
                var _a, _b, _c, _d, _e, _f, _g, _h, _j;
                // TODO only run this condition during dev.
                if (!didSetLocalizationResourceRun) {
                    throw new Error('Dev error: Run setLocalizationResource() first');
                }
                data.supported_locales = (_a = data.supported_locales) !== null && _a !== void 0 ? _a : {};
                data.supported_locales[locale] = {
                    locale,
                    last_updated: new Date(),
                    version: 1,
                    status: types_1.LocalizationStatus.IN_PROGRESS,
                    localization_origin: origin,
                    localization_origin_details: originDetails !== null && originDetails !== void 0 ? originDetails : null,
                };
                // Handle version bump up and set status to IN_PROGRESS
                const currLocalizationResource = (_b = entry === null || entry === void 0 ? void 0 : entry.localization_resources) === null || _b === void 0 ? void 0 : _b[locale];
                if (((_c = data.localization_resources) === null || _c === void 0 ? void 0 : _c[locale]) && currLocalizationResource) {
                    const currVersion = (_f = (_e = (_d = entry === null || entry === void 0 ? void 0 : entry.supported_locales) === null || _d === void 0 ? void 0 : _d[locale]) === null || _e === void 0 ? void 0 : _e.version) !== null && _f !== void 0 ? _f : 0;
                    const currStatus = (_j = (_h = (_g = entry === null || entry === void 0 ? void 0 : entry.supported_locales) === null || _g === void 0 ? void 0 : _g[locale]) === null || _h === void 0 ? void 0 : _h.status) !== null && _j !== void 0 ? _j : types_1.LocalizationStatus.IN_PROGRESS;
                    data.supported_locales[locale].version = currVersion + (isDiff() ? 1 : 0);
                    data.supported_locales[locale].status = isDiff() ? types_1.LocalizationStatus.IN_PROGRESS : currStatus;
                }
            };
            const setSortIndex = () => {
                // Cleanup: clear old sort index to avoid stale ordering metadata.
                cleanupData.metadata = { localization_resources_sort_index: null };
                hasCleanup = hasCleanup || !!entry; // Only attempt cleanup if a document exists.
                data.metadata = {
                    localization_resources_sort_index: {
                        namespaces: Object.fromEntries(namespaces.map(({ namespace }, index) => [namespace, index])),
                        messages: Object.fromEntries(namespaces.map(({ namespace, messages }) => [
                            namespace,
                            Object.fromEntries(Object.keys(messages).map((key, index) => [key, index])),
                        ])),
                    },
                };
            };
            const setLocalizationResourceHistory = () => {
                var _a, _b, _c, _d, _e, _f, _g, _h, _j;
                const isNew = !((_a = entry === null || entry === void 0 ? void 0 : entry.localization_resources) === null || _a === void 0 ? void 0 : _a[locale]);
                if (isDiff() || isNew) {
                    const { version, status, last_updated } = (_c = (_b = data.supported_locales) === null || _b === void 0 ? void 0 : _b[locale]) !== null && _c !== void 0 ? _c : {};
                    if (!version || !status || !last_updated) {
                        throw new Error('Internal error');
                    }
                    const base_locale_version = (_f = (_e = (_d = entry === null || entry === void 0 ? void 0 : entry.supported_locales) === null || _d === void 0 ? void 0 : _d[baseLocale]) === null || _e === void 0 ? void 0 : _e.version) !== null && _f !== void 0 ? _f : (_h = (_g = data.supported_locales) === null || _g === void 0 ? void 0 : _g[baseLocale]) === null || _h === void 0 ? void 0 : _h.version;
                    if (!base_locale_version) {
                        throw new Error('Internal error');
                    }
                    localizationResourceDataSet.add(Object.assign({ locale,
                        base_locale_version,
                        version,
                        status,
                        last_updated, localization_resource: (_j = data.localization_resources) === null || _j === void 0 ? void 0 : _j[locale], localization_origin: origin, localization_origin_details: originDetails !== null && originDetails !== void 0 ? originDetails : null }, ((entry === null || entry === void 0 ? void 0 : entry.library_resource_id) ? { library_resource_id: entry === null || entry === void 0 ? void 0 : entry.library_resource_id } : {})));
                }
            };
            if (baseLocale === locale) {
                // Base Locale
                setInitialData();
                setLocalizationResource();
                invalidateAllLocales();
                setSupportedLocale();
                setSortIndex();
                setLocalizationResourceHistory();
            }
            else {
                // Any other locales
                setLocalizationResource();
                setSupportedLocale();
                setLocalizationResourceHistory();
            }
            if (typeof transaction === 'object' && transaction !== null && data.supported_locales) {
                const localeVersions = (_b = this.transactionLocaleVersions.get(transaction)) !== null && _b !== void 0 ? _b : new Map();
                Object.entries(data.supported_locales).forEach(([supportedLocale, supportedLocaleData]) => {
                    if (typeof supportedLocaleData.version === 'number') {
                        localeVersions.set(supportedLocale, supportedLocaleData.version);
                    }
                });
                this.transactionLocaleVersions.set(transaction, localeVersions);
            }
            // First, apply targeted cleanup updates to avoid stale data.
            if (hasCleanup) {
                yield this.updateDatabaseEntry(cleanupData, transaction);
            }
            yield this.updateDatabaseEntry(data, transaction);
            // Save localization_resource history
            if (localizationResourceDataSet.size) {
                for (const localizationResourceData of localizationResourceDataSet) {
                    const { locale, version } = localizationResourceData;
                    if (!locale || !version) {
                        throw new Error(`Internal error ${JSON.stringify(localizationResourceData)}`);
                    }
                    yield this.setDatabaseLocalizationResourceEntry(locale, version, localizationResourceData, transaction);
                }
            }
        });
    }
    rawResetAllMessages(allLocalesNamespacesEntries) {
        return __awaiter(this, void 0, void 0, function* () {
            const data = {
                base_locale: this.initBaseLocale,
                display_name: this.initDisplayName,
            };
            for (const [locale, namespaces] of Object.entries(allLocalesNamespacesEntries)) {
                if (!data.supported_locales) {
                    data.supported_locales = {};
                }
                if (!data.localization_resources) {
                    data.localization_resources = {};
                }
                data.supported_locales[locale] = {
                    locale,
                    last_updated: new Date(),
                    version: 1,
                    status: types_1.LocalizationStatus.IN_PROGRESS,
                    localization_origin: types_1.LocalizationOrigin.USER,
                    localization_origin_details: null,
                };
                data.localization_resources[locale] = namespaces;
            }
            yield this.setDatabaseEntry(data);
        });
    }
    setStatus(locale, newStatus) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c, _d, _e, _f, _g, _h;
            const entry = yield this.getDatabaseEntry();
            if (!entry) {
                throw new utils_1.CodedError('E_NOT_FOUND', 'Document not found');
            }
            const currStatus = (_b = (_a = entry.supported_locales) === null || _a === void 0 ? void 0 : _a[locale]) === null || _b === void 0 ? void 0 : _b.status;
            if (currStatus === undefined) {
                throw new utils_1.CodedError('E_INVALID_LOCALE', `Cannot change status of locale '${locale}' that has no content yet`);
            }
            if (currStatus === newStatus) {
                // if newStatus is the same as existing, do nothing.
                return;
            }
            if (newStatus === types_1.LocalizationStatus.NOT_STARTED) {
                throw new utils_1.CodedError('E_STATE', `Can't change from "${currStatus}" to "${types_1.LocalizationStatus.NOT_STARTED}"`);
            }
            const data = {
                supported_locales: { [locale]: { status: newStatus } },
            };
            // If changing status from READY to IN_REVIEW or IN_PROGRESS, bump up the version, and set a new localization resource history documentation.
            const version = (_e = (_d = (_c = entry.supported_locales) === null || _c === void 0 ? void 0 : _c[locale]) === null || _d === void 0 ? void 0 : _d.version) !== null && _e !== void 0 ? _e : 0;
            if (currStatus === types_1.LocalizationStatus.READY && ((_f = data === null || data === void 0 ? void 0 : data.supported_locales) === null || _f === void 0 ? void 0 : _f[locale])) {
                const newVersion = version + 1;
                data.supported_locales[locale].version = newVersion;
                const localizationResourceData = yield this.getDatabaseLocalizationResourceEntry(locale, version);
                if (!localizationResourceData) {
                    throw new Error('Internal error');
                }
                localizationResourceData.version = newVersion;
                localizationResourceData.status = newStatus;
                const isStudyPdfResource = !!((_h = (_g = localizationResourceData.localization_resource) === null || _g === void 0 ? void 0 : _g.study_pdf) === null || _h === void 0 ? void 0 : _h.resourceId);
                if (!isStudyPdfResource) {
                    delete localizationResourceData.library_translation_id;
                }
                yield this.setDatabaseLocalizationResourceEntry(locale, newVersion, localizationResourceData);
            }
            else {
                // Otherwise, don't bump up the version, and update the status in the localization resource history document.
                const localizationResourceData = { status: newStatus };
                yield this.updateDatabaseLocalizationResourceEntry(locale, version, localizationResourceData);
            }
            yield this.updateDatabaseEntry(data);
        });
    }
    setResourceId(libraryResourceId) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.updateDatabaseEntry({ library_resource_id: libraryResourceId });
        });
    }
    setTranslationId(locale, libraryResourceId, libraryTranslationId, version, options) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c, _d, _e;
            const transaction = options === null || options === void 0 ? void 0 : options.transaction;
            const entry = yield this.getDatabaseEntry();
            const transactionVersion = typeof transaction === 'object' && transaction !== null
                ? (_a = this.transactionLocaleVersions.get(transaction)) === null || _a === void 0 ? void 0 : _a.get(locale)
                : undefined;
            const _version = (_e = (_b = version !== null && version !== void 0 ? version : transactionVersion) !== null && _b !== void 0 ? _b : (_d = (_c = entry === null || entry === void 0 ? void 0 : entry.supported_locales) === null || _c === void 0 ? void 0 : _c[locale]) === null || _d === void 0 ? void 0 : _d.version) !== null && _e !== void 0 ? _e : 1;
            yield this.updateDatabaseLocalizationResourceEntry(locale, _version, {
                library_resource_id: libraryResourceId,
                library_translation_id: libraryTranslationId,
            }, transaction);
        });
    }
    setPreview(options) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.updateDatabaseEntry({ metadata: { preview: options } });
        });
    }
    getId() {
        return this.id;
    }
    /**
     * Returns the asset type inferred from the localization id.
     * The id format is typically: "<type>.<id>~<version>" (e.g., "activities.some_activity~1_0").
     */
    getAssetType() {
        var _a, _b;
        const type = (_b = (_a = this.getId()) === null || _a === void 0 ? void 0 : _a.split('.')) === null || _b === void 0 ? void 0 : _b[0];
        return Object.values(types_1.AssetTypes).includes(type) ? type : undefined;
    }
    getDisplayName(entry) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c, _d, _e, _f, _g, _h;
            const assetType = this.getAssetType();
            let thisEntry = entry;
            if (!thisEntry) {
                thisEntry = yield this.getDatabaseEntry();
            }
            if (assetType === types_1.AssetTypes.RESOURCES || assetType === types_1.AssetTypes.RESOURCES_PDF) {
                return (0, utils_1.getDocumentDisplayName)(thisEntry);
            }
            if (assetType === types_1.AssetTypes.ACTIVITY) {
                return (((_e = (_d = (_c = (_b = (_a = thisEntry === null || thisEntry === void 0 ? void 0 : thisEntry.metadata) === null || _a === void 0 ? void 0 : _a.preview) === null || _b === void 0 ? void 0 : _b.info) === null || _c === void 0 ? void 0 : _c.list) === null || _d === void 0 ? void 0 : _d.find(({ display_name }) => display_name === 'Display name')) === null || _e === void 0 ? void 0 : _e.value) ||
                    (0, utils_1.getDocumentDisplayName)(thisEntry));
            }
            return ((_h = (_g = (_f = thisEntry === null || thisEntry === void 0 ? void 0 : thisEntry.metadata) === null || _f === void 0 ? void 0 : _f.preview) === null || _g === void 0 ? void 0 : _g.info) === null || _h === void 0 ? void 0 : _h.display_name) || (0, utils_1.getDocumentDisplayName)(thisEntry);
        });
    }
    getBaseLocale(entry) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            let thisEntry = entry;
            if (!thisEntry) {
                thisEntry = yield this.getDatabaseEntry();
            }
            return (_b = (_a = thisEntry === null || thisEntry === void 0 ? void 0 : thisEntry.base_locale) !== null && _a !== void 0 ? _a : this.initBaseLocale) !== null && _b !== void 0 ? _b : defaultInitBaseLocale;
        });
    }
    getMessages(locale, options) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b, _c, _d, _e, _f;
            const { version, stableOutput, addIdentityMetadata, setLastExportedAt } = options !== null && options !== void 0 ? options : {};
            if (addIdentityMetadata && !stableOutput) {
                throw new Error('The addIdentityMetadata option can only be used when stableOutput is also enabled');
            }
            const entry = (_a = options === null || options === void 0 ? void 0 : options.entry) !== null && _a !== void 0 ? _a : (yield this.getDatabaseEntry());
            if (!entry) {
                throw new utils_1.CodedError('E_NOT_FOUND', 'Document not found');
            }
            let localizationResource = (_b = entry === null || entry === void 0 ? void 0 : entry.localization_resources) === null || _b === void 0 ? void 0 : _b[locale];
            const latestVersion = (_e = (_d = (_c = entry.supported_locales) === null || _c === void 0 ? void 0 : _c[locale]) === null || _d === void 0 ? void 0 : _d.version) !== null && _e !== void 0 ? _e : 0;
            if (version !== undefined) {
                if (version < 1 || version > latestVersion) {
                    throw new RangeError(`Version must be between 1 and ${latestVersion}. Provided version: ${version}`);
                }
                else if (version < latestVersion) {
                    const localizationResourceEntry = yield this.getDatabaseLocalizationResourceEntry(locale, version);
                    if (!localizationResourceEntry) {
                        throw new utils_1.CodedError('E_NOT_FOUND', `Localization content for locale '${locale}' version ${version} not found`);
                    }
                    localizationResource = localizationResourceEntry === null || localizationResourceEntry === void 0 ? void 0 : localizationResourceEntry.localization_resource;
                }
            }
            if (!localizationResource) {
                return null;
            }
            if (stableOutput) {
                const orderedNamespaces = new Map();
                if (addIdentityMetadata) {
                    orderedNamespaces.set('_identity', { id: this.getId(), locale });
                }
                const sortIndex = ((_f = entry.metadata) === null || _f === void 0 ? void 0 : _f.localization_resources_sort_index) || {};
                const sortedNamespaces = Object.keys(localizationResource).sort((a, b) => { var _a, _b, _c, _d; return ((_b = (_a = sortIndex.namespaces) === null || _a === void 0 ? void 0 : _a[a]) !== null && _b !== void 0 ? _b : 0) - ((_d = (_c = sortIndex.namespaces) === null || _c === void 0 ? void 0 : _c[b]) !== null && _d !== void 0 ? _d : 0); });
                for (const namespace of sortedNamespaces) {
                    const messages = localizationResource[namespace];
                    const orderedMessages = new Map();
                    Object.keys(messages)
                        .sort((a, b) => { var _a, _b, _c, _d, _e, _f; return ((_c = (_b = (_a = sortIndex.messages) === null || _a === void 0 ? void 0 : _a[namespace]) === null || _b === void 0 ? void 0 : _b[a]) !== null && _c !== void 0 ? _c : 0) - ((_f = (_e = (_d = sortIndex.messages) === null || _d === void 0 ? void 0 : _d[namespace]) === null || _e === void 0 ? void 0 : _e[b]) !== null && _f !== void 0 ? _f : 0); })
                        .forEach((key) => {
                        orderedMessages.set(key, messages[key]);
                    });
                    orderedNamespaces.set(namespace, Object.fromEntries(orderedMessages));
                }
                localizationResource = Object.fromEntries(orderedNamespaces);
            }
            if (setLastExportedAt) {
                yield this.updateDatabaseEntry({ supported_locales: { [locale]: { last_exported_at: new Date() } } });
            }
            return localizationResource;
        });
    }
    getLocaleHistory(locale) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.getDatabaseLocalizationResourceEntries(locale);
        });
    }
    runDatabaseTransaction(fn) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.databaseTransactionRunner) {
                return yield fn(undefined);
            }
            return yield this.databaseTransactionRunner(fn);
        });
    }
    databaseEntryExists() {
        return __awaiter(this, void 0, void 0, function* () {
            throw new Error('databaseEntryExists not implemented');
        });
    }
    getDatabaseEntry() {
        return __awaiter(this, void 0, void 0, function* () {
            throw new Error('getDatabaseEntry not implemented');
        });
    }
    setDatabaseEntry(entry, transaction) {
        return __awaiter(this, void 0, void 0, function* () {
            throw new Error(`setDatabaseEntry not implemented, ${entry}, ${transaction}`);
        });
    }
    updateDatabaseEntry(entry, transaction) {
        return __awaiter(this, void 0, void 0, function* () {
            throw new Error(`updateDatabaseEntry not implemented, ${entry}, ${transaction}`);
        });
    }
    getDatabaseLocalizationResourceEntry(locale, version) {
        return __awaiter(this, void 0, void 0, function* () {
            throw new Error(`getLocalizationResourceDatabaseEntry not implemented, ${locale}, ${version}`);
        });
    }
    getDatabaseLocalizationResourceEntries(locale) {
        return __awaiter(this, void 0, void 0, function* () {
            throw new Error(`getDatabaseLocalizationResourceEntries not implemented, ${locale}`);
        });
    }
    setDatabaseLocalizationResourceEntry(locale, version, entry, transaction) {
        return __awaiter(this, void 0, void 0, function* () {
            throw new Error(`setDatabaseLocalizationResourceEntry not implemented, ${locale}, ${version} ${entry}, ${transaction}`);
        });
    }
    updateDatabaseLocalizationResourceEntry(locale, version, entry, transaction) {
        return __awaiter(this, void 0, void 0, function* () {
            throw new Error(`updateDatabaseLocalizationResourceEntry not implemented, ${locale}, ${version} ${entry}, ${transaction}`);
        });
    }
}
exports.Localization = Localization;
//# sourceMappingURL=localization.js.map